/**
 * Practice service: asks ear-training questions, plays them through the
 * engine, records answers locally and adapts difficulty (F-LEARN-05/06).
 */
import { errorMessage } from "../core/errors";
import { createId } from "../core/ids";
import { type Rng } from "../core/random";
import { choicesFor, makeQuestion, type Choice, type ExerciseKind, type Question } from "../learning/exercises";
import { INITIAL_STAIRCASE, nextStaircase, type StaircaseState } from "../learning/staircase";
import type { PracticeAttempt } from "../learning/stats";
import type { EngineController } from "./controller";
import { Store } from "./store";

export interface PracticeState {
  loaded: boolean;
  staircase: Record<ExerciseKind, StaircaseState>;
  attempts: Record<ExerciseKind, PracticeAttempt[]>;
  dailyGoal: number;
}

export interface AnswerResult { correct: boolean; answer: Choice; reveal: string; levelChange: -1 | 0 | 1; }

const STAIRCASE_KEY = (k: ExerciseKind) => `practice:staircase:${k}`;
const GOAL_KEY = "practice:dailyGoal";

export class PracticeService {
  readonly state = new Store<PracticeState>({
    loaded: false,
    staircase: { interval: INITIAL_STAIRCASE, chord: INITIAL_STAIRCASE },
    attempts: { interval: [], chord: [] },
    dailyGoal: 20,
  });
  private current: { question: Question; askedAt: number } | null = null;
  /** Last asked answer per exercise, so consecutive questions differ (survives answering). */
  private lastAnswer: Partial<Record<ExerciseKind, string>> = {};

  constructor(private c: EngineController, private rng: Rng = Math.random, private now: () => number = () => performance.now()) {}

  get persistent(): boolean { return this.c.storage !== null; }

  async load(): Promise<void> {
    const s = this.c.storage;
    if (!s) { this.patch({ loaded: true }); return; }
    try {
      const [ia, ca, is, cs, goal] = await Promise.all([
        s.listAttempts("interval"), s.listAttempts("chord"),
        s.getMeta<StaircaseState>(STAIRCASE_KEY("interval")), s.getMeta<StaircaseState>(STAIRCASE_KEY("chord")), s.getMeta<number>(GOAL_KEY),
      ]);
      this.patch({
        loaded: true,
        attempts: { interval: ia, chord: ca },
        staircase: { interval: validStaircase(is), chord: validStaircase(cs) },
        dailyGoal: typeof goal === "number" && goal > 0 ? goal : 20,
      });
    } catch (err) {
      this.c.notify("warning", `Practice history could not be loaded: ${errorMessage(err)}`);
      this.patch({ loaded: true });
    }
  }

  choices(kind: ExerciseKind): Choice[] {
    return choicesFor(kind, this.state.get().staircase[kind].level);
  }

  /** Create and play a new question at the learner's current level. */
  ask(kind: ExerciseKind): Question {
    const level = this.state.get().staircase[kind].level;
    const question = makeQuestion(kind, level, this.rng, this.lastAnswer[kind]);
    this.lastAnswer[kind] = question.answerId;
    this.current = { question, askedAt: this.now() };
    this.play(question);
    return question;
  }

  play(q: Question): void {
    this.c.previewSequence(q.steps, q.steps.length > 1 ? 0.75 : 1.4, { transpose: false, gateRatio: 0.85 });
  }

  /** Record an answer for the current question. Returns null if no question is open. */
  async answer(givenId: string): Promise<AnswerResult | null> {
    if (!this.current) return null;
    const { question, askedAt } = this.current;
    this.current = null;
    const correct = givenId === question.answerId;
    const kind = question.kind;
    const before = this.state.get().staircase[kind];
    const after = nextStaircase(before, correct);
    const attempt: PracticeAttempt = {
      id: createId(), exercise: kind, level: question.level, answerId: question.answerId, givenId, correct,
      responseMs: Math.max(0, Math.round(this.now() - askedAt)), at: new Date().toISOString(),
    };
    const s = this.state.get();
    this.patch({ staircase: { ...s.staircase, [kind]: after }, attempts: { ...s.attempts, [kind]: [...s.attempts[kind], attempt] } });
    try {
      await this.c.storage?.addAttempt(attempt);
      await this.c.storage?.setMeta(STAIRCASE_KEY(kind), after);
    } catch (err) {
      this.c.notify("warning", `Could not save practice result: ${errorMessage(err)}`);
    }
    const answer = choicesFor(kind, question.level).find((c) => c.id === question.answerId)!;
    return { correct, answer, reveal: question.reveal, levelChange: Math.sign(after.level - before.level) as -1 | 0 | 1 };
  }

  async setDailyGoal(n: number): Promise<void> {
    const goal = Math.max(1, Math.min(500, Math.round(n)));
    this.patch({ dailyGoal: goal });
    await this.c.storage?.setMeta(GOAL_KEY, goal).catch(() => undefined);
  }

  private patch(p: Partial<PracticeState>): void {
    this.state.set({ ...this.state.get(), ...p });
  }
}

/** Stored progress is untrusted (could be from an older version): validate shape. */
function validStaircase(v: unknown): StaircaseState {
  const s = v as StaircaseState | undefined;
  if (s && Number.isInteger(s.level) && s.level >= 1 && s.level <= 4 && Number.isInteger(s.streak) && Array.isArray(s.recent)) {
    return { level: s.level, streak: Math.max(0, Math.min(3, s.streak)), recent: s.recent.filter((r) => typeof r === "boolean").slice(-3) };
  }
  return INITIAL_STAIRCASE;
}

